import { studyQueryKeys } from '@/config/queryKeys';
import { PROTEOFORM_DEFAULT_THRESHOLD_CPM, TABLE_PAGE_SIZE_OPTIONS } from '@/constants';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import {
  PaginatedSampleProteoformsResponse,
  SampleProteoformPublic,
  StudySampleProteoformSearchRequest,
} from '@/types';
import { flushColumnState } from '@/utils/flushColumnState';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { ColDef, GridOptions } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSampleTableGrid } from '../../Studies/components/useSampleTableGrid';
import { NUMBER_FILTER_PARAMS, TEXT_FILTER_PARAMS } from '../../Studies/utils/sharedColumnDefs';

interface BroadscaleSamplesDetailTableProps {
  orgId: string;
  studyId: string;
  assayId: string;
}

const paginationPageSizeKey = (studyId: string) => `study-${studyId}-broadscale-detail-pageSize`;
const VALID_SORT_FIELDS = ['sample_name', 'instrument_run_name', 'proteoform_name', 'proteoform_abundance'];
const DEFAULT_PAGE_SIZE = 25;

const gridOptions: GridOptions = {
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  suppressMovableColumns: false,
  suppressDragLeaveHidesColumns: false,
};

export const BroadscaleSamplesDetailTable = function BroadscaleSamplesDetailTable({
  orgId,
  studyId,
  assayId,
}: BroadscaleSamplesDetailTableProps) {
  const { t } = useTranslation('studies');
  const gridRef = useRef<AgGridReact<SampleProteoformPublic>>(null);

  const datasource = useMemo(
    () =>
      createAgGridDatasource<
        SampleProteoformPublic,
        PaginatedSampleProteoformsResponse,
        StudySampleProteoformSearchRequest
      >(studyQueryKeys.endpoints.proteoformsSearch(orgId, studyId), ({ limit, skip, sortBy, sortOrder, filters }) => {
        const validSortBy = sortBy && VALID_SORT_FIELDS.includes(sortBy) ? sortBy : 'sample_name';
        // The assay is scoped by the required top-level `assay_id`, not by a filter — mirrors
        // AssaySamplesTable. Omitting it is a 422.
        return {
          assay_id: assayId,
          skip,
          limit,
          sort_by: validSortBy as StudySampleProteoformSearchRequest['sort_by'],
          sort_order: (sortOrder ?? 'asc') as 'asc' | 'desc',
          threshold_cpm: PROTEOFORM_DEFAULT_THRESHOLD_CPM,
          filters: filters as StudySampleProteoformSearchRequest['filters'],
        };
      }),
    [orgId, studyId, assayId],
  );

  const {
    defaultColDef,
    handleFilterChanged,
    handleFirstDataRendered,
    handleGridReady,
    handleModelUpdated,
    handlePaginationChanged,
    pageSize,
  } = useSampleTableGrid<SampleProteoformPublic>({
    studyId,
    tableId: 'broadscale-samples-detail-table',
    validSortFields: VALID_SORT_FIELDS,
    paginationPageSizeKeyFn: paginationPageSizeKey,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    datasource,
    autoHeight: true,
  });

  const colDefs: ColDef<SampleProteoformPublic>[] = useMemo(
    () => [
      {
        field: 'sample_name',
        headerName: t('columnHeaders.sampleName'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'proteoform_name',
        headerName: t('columnHeaders.protein'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'proteoform_abundance',
        headerName: t('columnHeaders.quantityCpm'),
        filter: 'agNumberColumnFilter',
        filterParams: NUMBER_FILTER_PARAMS,
        resizable: true,
        sortable: true,
        valueFormatter: ({ value }) => value?.toLocaleString() ?? '',
      },
      {
        // TODO: add field when SampleProteoformPublic exposes protein_detection_fdr
        colId: 'protein_detection_fdr',
        headerName: 'Protein Detection FDR',
        resizable: true,
        sortable: false,
        filter: false,
      },
      {
        // TODO: add field when SampleProteoformPublic exposes detection_evidence
        colId: 'detection_evidence',
        headerName: 'Detection Evidence',
        resizable: true,
        sortable: false,
        filter: false,
      },
      {
        // TODO: add field when SampleProteoformPublic exposes species
        colId: 'species',
        headerName: t('columnHeaders.species'),
        resizable: true,
        sortable: false,
        filter: false,
      },
    ],
    [t],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  return (
    <Table
      columnDefs={colDefs}
      defaultColDef={defaultColDef}
      domLayout="autoHeight"
      enableAdvancedFilter={false}
      enableResponsiveResize={false}
      getRowId={(params: { data: SampleProteoformPublic }) => `${params.data.sample_id}-${params.data.proteoform_name}`}
      gridOptions={gridOptions}
      onFilterChanged={handleFilterChanged}
      onFirstDataRendered={handleFirstDataRendered}
      onGridPreDestroyed={e => flushColumnState(e.api, 'broadscale-samples-detail-table')}
      onGridReady={handleGridReady}
      onModelUpdated={handleModelUpdated}
      onPaginationChanged={handlePaginationChanged}
      pagination
      paginationPageSize={pageSize}
      paginationPageSizeSelector={TABLE_PAGE_SIZE_OPTIONS}
      ref={gridRef}
      resizeDebounceMs={150}
      rowModelType="serverSide"
      tableId="broadscale-samples-detail-table"
    />
  );
};
